import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Zap, Shield, Crown, Clock } from 'lucide-react';

const GoldLoan = () => {
  const features = [
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Fast Approval",
      description: "Get loan approval and disbursal within 30 minutes"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Safe Storage",
      description: "Your gold ornaments are stored in secure bank lockers"
    },
    {
      icon: <Crown className="h-6 w-6" />,
      title: "No Income Proof",
      description: "No documentation required for income verification"
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "Flexible Repayment",
      description: "Choose from various repayment options as per convenience"
    }
  ];

  const eligibility = [
    "Age: 18 to 75 years",
    "Gold Purity: Minimum 18 karat gold ornaments",
    "Identity Proof: Valid government-issued ID",
    "Address Proof: Utility bills or rental agreement",
    "No Income Documentation Required",
    "Indian Resident or NRI"
  ];

  const documents = [
    "Identity Proof (Aadhaar/PAN/Driving License)",
    "Address Proof (Utility Bills/Passport/Aadhaar)",
    "Gold Ornaments (18K or above)",
    "Recent Passport Size Photographs",
    "NACH/ECS Mandate Form",
    "Loan Application Form"
  ];

  const goldTypes = [
    "Gold Ornaments/Jewelry",
    "Gold Coins (999 purity)",
    "Gold Bars/Biscuits",
    "Antique Gold Items",
    "Designer Gold Jewelry",
    "Traditional Gold Ornaments"
  ];

  const repaymentOptions = [
    {
      type: "Regular EMI",
      description: "Fixed monthly installments for the entire loan tenure",
      suitable: "For salaried individuals with regular income"
    },
    {
      type: "Bullet Payment",
      description: "Pay only interest monthly, principal at maturity",
      suitable: "For business owners with seasonal income"
    },
    {
      type: "Part Payment",
      description: "Make partial payments anytime to reduce interest burden",
      suitable: "For those with irregular but sufficient income"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
                  Instant Cash Against
                  <span className="block" style={{ color: '#340C7F' }}>
                    Your Gold
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Get instant cash against your gold ornaments with competitive 
                  interest rates starting from 13% p.a. Quick, secure, and hassle-free process.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>Instant</p>
                  <p className="text-sm text-gray-600">Loan Amount</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>13%*</p>
                  <p className="text-sm text-gray-600">Interest Rate</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>30 Min</p>
                  <p className="text-sm text-gray-600">Approval Time</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>75%</p>
                  <p className="text-sm text-gray-600">Loan to Value</p>
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white rounded-lg hover:opacity-90 transition-opacity shadow-lg"
                style={{ backgroundColor: '#340C7F' }}
              >
                Apply Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1611107683227-e9060eccd846?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxnb2xkJTIwamV3ZWxyeXxlbnwwfHx8fDE3NTM4MTkyNzd8MA&ixlib=rb-4.1.0&q=85" 
                alt="Premium Gold Jewelry Collection" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Why Choose Our <span style={{ color: '#340C7F' }}>Gold Loan?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center space-y-4 p-6 rounded-xl hover:shadow-lg transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full text-white" 
                     style={{ backgroundColor: '#340C7F' }}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Simple <span style={{ color: '#340C7F' }}>3-Step Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get instant cash against your gold in just 30 minutes with our simplified process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Gold Evaluation',
                description: 'Bring your gold ornaments to our branch for purity and weight verification',
                time: '10 Minutes'
              },
              {
                step: '02',
                title: 'Loan Processing',
                description: 'Complete simple documentation and loan agreement formalities',
                time: '15 Minutes'
              },
              {
                step: '03',
                title: 'Cash Disbursal',
                description: 'Receive instant cash transfer to your bank account or cash',
                time: '5 Minutes'
              }
            ].map((process, index) => (
              <div key={index} className="bg-white rounded-xl p-8 text-center shadow-lg">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full text-white text-xl font-bold mb-6"
                     style={{ backgroundColor: '#340C7F' }}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{process.title}</h3>
                <p className="text-gray-600 mb-4">{process.description}</p>
                <div className="inline-block px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                  {process.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gold Types & Repayment */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Accepted Gold Types */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Accepted <span style={{ color: '#340C7F' }}>Gold Types</span>
                </h2>
                <p className="text-lg text-gray-600">
                  We accept various types of gold ornaments and items as collateral.
                </p>
              </div>

              {/* Premium Gold Collection Image */}
              <div className="mb-8">
                <img 
                  src="https://images.unsplash.com/photo-1624365168785-c65be9114821?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxnb2xkJTIwY29pbnN8ZW58MHx8fHwxNzUzODE5Mjg2fDA&ixlib=rb-4.1.0&q=85" 
                  alt="Premium Gold Coins and Bars Collection" 
                  className="w-full h-64 object-cover rounded-xl shadow-lg"
                />
              </div>

              <div className="grid grid-cols-1 gap-4">
                {goldTypes.map((type, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <p className="font-medium text-gray-900">{type}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Repayment Options */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Flexible <span style={{ color: '#340C7F' }}>Repayment</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Choose the repayment option that best suits your financial situation.
                </p>
              </div>

              {/* Premium Gold Jewelry Image */}
              <div className="mb-8">
                <img 
                  src="https://images.unsplash.com/photo-1623321673989-830eff0fd59f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxnb2xkJTIwamV3ZWxyeXxlbnwwfHx8fDE3NTM4MTkyNzd8MA&ixlib=rb-4.1.0&q=85" 
                  alt="Elegant Gold Jewelry for Loan" 
                  className="w-full h-64 object-cover rounded-xl shadow-lg"
                />
              </div>

              <div className="space-y-6">
                {repaymentOptions.map((option, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{option.type}</h3>
                    <p className="text-gray-700 mb-3">{option.description}</p>
                    <p className="text-sm font-medium" style={{ color: '#340C7F' }}>
                      {option.suitable}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility & Documents Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Eligibility Criteria */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Eligibility <span style={{ color: '#340C7F' }}>Criteria</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Simple eligibility requirements for gold loan approval.
                </p>
              </div>

              <ul className="space-y-4">
                {eligibility.map((criterion, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{criterion}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Required Documents */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Required <span style={{ color: '#340C7F' }}>Documents</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Minimal documentation required for quick gold loan processing.
                </p>
              </div>

              <ul className="space-y-4">
                {documents.map((document, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{document}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Security */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Your Gold is <span style={{ color: '#340C7F' }}>Safe & Secure</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Bank-Grade Security",
                description: "Your gold is stored in highly secure bank lockers with 24/7 surveillance"
              },
              {
                title: "Insurance Coverage",
                description: "Complete insurance coverage for your gold ornaments during the loan period"
              },
              {
                title: "Same Gold Return",
                description: "Get back the exact same gold ornaments that you pledged with us"
              }
            ].map((security, index) => (
              <div key={index} className="text-center space-y-4 p-6 rounded-xl bg-gray-50">
                <Shield className="h-12 w-12 mx-auto" style={{ color: '#340C7F' }} />
                <h3 className="text-xl font-semibold text-gray-900">{security.title}</h3>
                <p className="text-gray-600">{security.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gold Rate & Calculation Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Gold Loan <span style={{ color: '#340C7F' }}>Rate Calculator</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understand how we calculate your loan amount based on current gold rates
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Current Gold Rates</h3>
                <div className="space-y-4">
                  {[
                    { karat: "24K Gold", rate: "₹6,150/gram", purity: "99.9%", description: "Pure gold coins & bars" },
                    { karat: "22K Gold", rate: "₹5,640/gram", purity: "91.6%", description: "Most jewelry & ornaments" },
                    { karat: "20K Gold", rate: "₹5,125/gram", purity: "83.3%", description: "Some traditional jewelry" },
                    { karat: "18K Gold", rate: "₹4,610/gram", purity: "75.0%", description: "Modern jewelry designs" }
                  ].map((gold, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-semibold text-gray-900">{gold.karat}</h4>
                        <p className="text-sm text-gray-600">{gold.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold" style={{ color: '#340C7F' }}>{gold.rate}</p>
                        <p className="text-sm text-gray-600">{gold.purity} purity</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-4">*Rates updated daily and may vary based on market conditions</p>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Loan Amount Calculation</h3>
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Example Calculation</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Gold Weight:</span>
                        <span className="font-semibold">50 grams</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Gold Purity:</span>
                        <span className="font-semibold">22K (91.6%)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Current Rate:</span>
                        <span className="font-semibold">₹5,640/gram</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Market Value:</span>
                        <span className="font-semibold">₹2,82,000</span>
                      </div>
                      <hr className="my-2" />
                      <div className="flex justify-between text-lg">
                        <span className="text-gray-900 font-semibold">Loan Amount (75%):</span>
                        <span className="font-bold" style={{ color: '#340C7F' }}>₹2,11,500</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Factors Affecting Loan Amount</h4>
                    <ul className="space-y-2">
                      {[
                        "Gold purity (18K to 24K accepted)",
                        "Current market rates",
                        "Loan-to-value ratio (up to 75%)",
                        "Gold ornament weight",
                        "Making charges deduction"
                      ].map((factor, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-gray-700">{factor}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Branch Locations & Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Visit Our <span style={{ color: '#340C7F' }}>Gold Loan Centers</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Convenient locations with state-of-the-art facilities for gold evaluation and loan processing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                city: "Mumbai",
                address: "Shop No. 15, Ground Floor, Clover Park, Vashi, Navi Mumbai - 400703",
                timings: "9:00 AM - 6:00 PM (Mon-Sat)",
                facilities: ["Digital Gold Testing", "Secure Lockers", "Instant Cash", "Expert Valuers"],
                contact: "+91 98765 43210"
              },
              {
                city: "Pune",
                address: "Office No. 201, 2nd Floor, Sai Plaza, FC Road, Pune - 411005",
                timings: "9:30 AM - 5:30 PM (Mon-Sat)",
                facilities: ["Advanced Testing Equipment", "Safe Storage", "Quick Processing", "Multilingual Staff"],
                contact: "+91 98765 43211"
              },
              {
                city: "Ahmedabad",
                address: "A-301, 3rd Floor, Business Hub, C.G. Road, Ahmedabad - 380009",
                timings: "10:00 AM - 6:00 PM (Mon-Sat)",
                facilities: ["Gold Purity Testing", "Secure Vaults", "Flexible Timings", "Customer Support"],
                contact: "+91 98765 43212"
              }
            ].map((branch, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900">{branch.city}</h3>
                    <p className="text-sm text-gray-600 mt-2">{branch.address}</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Timings:</p>
                      <p className="text-sm text-gray-600">{branch.timings}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-900">Contact:</p>
                      <p className="text-sm font-bold" style={{ color: '#340C7F' }}>{branch.contact}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-2">Facilities:</p>
                      <ul className="space-y-1">
                        {branch.facilities.map((facility, idx) => (
                          <li key={idx} className="flex items-center space-x-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            <span className="text-xs text-gray-700">{facility}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: '#340C7F' }}
                    >
                      Visit Branch
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#340C7F' }}>
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Need Instant Cash? Visit Our Branch Today!
          </h2>
          <p className="text-xl text-blue-100">
            Walk in with your gold ornaments and walk out with instant cash in just 30 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors shadow-lg transform hover:scale-105"
            >
              Apply for Gold Loan
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white rounded-lg hover:bg-white hover:text-gray-900 transition-colors transform hover:scale-105"
            >
              Find Branch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GoldLoan;